import React, { useState } from "react";

import { Button } from "@iwoca/orion";
import cn from "classnames";
import { Link } from "react-router";

import {
  OtpCodeField,
  OtpCodeForm,
} from "../Components/OtpCodeForm/OtpCodeForm";
import { SetupInstructions } from "../Components/SetupInstructions/SetupInstructions";
import { SetupStep } from "../Components/SetupStep/SetupStep";
import { useGetSetupMultifactorAuth } from "../hooks/useConnectMultifactorAuthentication";
import { useMultifactorAuthKey } from "../hooks/useMultifactorAuthKey";
import styles from "../MultifactorAuth.module.css";
import { SuccessMultifactorAuth } from "../SuccessMultifactorAuth/SuccessMultifactorAuth";
import { TwoFactorErrorResponse } from "../utils/ErrorHandling";

export const ConnectMultifactorAuth = () => {
  const [isSetup, setIsSetup] = useState<boolean>(false);
  const { multifactorAuthKey } = useMultifactorAuthKey();
  const { setupData, isSetupError, setupLoading, setupError } =
    useGetSetupMultifactorAuth({ authKey: multifactorAuthKey! });

  if (setupLoading) {
    return null;
  }

  if (isSetupError) {
    return <ErrorScreen error={setupError!} />;
  }

  if (isSetup) {
    return <SuccessMultifactorAuth />;
  }

  return (
    <div className={styles.modalLarge}>
      <SetupInstructions
        setupUri={setupData!.data.setup_uri}
        account={setupData!.data.account}
        setupKey={setupData!.data.key}
      >
        <OtpCodeForm onSuccess={() => setIsSetup(true)} type="sign up">
          {(onClick: () => void) => (
            <>
              <EnterCodeStep
                account={setupData!.data.account}
                onClick={onClick}
              />
              <SetupConfirmButton />
            </>
          )}
        </OtpCodeForm>
      </SetupInstructions>
    </div>
  );
};

export const EnterCodeStep = ({
  account,
  onClick,
}: {
  account: string;
  onClick?: () => void;
}) => (
  <SetupStep
    stepNumber={3}
    stepText="Enter your 6-digit code"
    stepSubtitle={`You'll see it under “iwocaPay ${account}”`}
    stepBody={
      <div className={styles.pushField}>
        <OtpCodeField onClick={onClick} name="otpCode"></OtpCodeField>
      </div>
    }
  ></SetupStep>
);

export const SetupConfirmButton = ({ disabled }: { disabled?: boolean }) => (
  <Button
    type="submit"
    className={styles.submitButtonShortSetup}
    disabled={disabled}
  >
    Confirm
  </Button>
);

const ErrorScreen = ({ error }: { error?: TwoFactorErrorResponse }) => {
  return (
    <div className={cn(styles.modalLarge, styles.errorModal)}>
      <h2 className={styles.somethingWrongTitle}>Something's gone wrong</h2>

      {error?.code === "TokenExpired" && <p>Your login session has expired</p>}

      <p className={styles.errorReason}>
        Sorry, we are unable to setup two factor authentication at this time.
        Please log in again and try again.
      </p>
      <Link to="/login" className={styles.link}>
        <Button
          type="submit"
          className={cn(styles.submitButton, styles.somethingWrongButton)}
        >
          Log in again
        </Button>
      </Link>
    </div>
  );
};
